import React, { useCallback, useEffect, useState } from "react";
import { radStudyApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Section } from "../../Components/UI/ui";
import { GATE, RAD_STATUS_LABEL, TERMINAL, modalityLabel } from "../../lib/lab";
import StudyDetail from "./StudyDetail";

// The scans of one radiology booking — the counterpart of the pathology
// "Result sheets" panel. Each scan is reported and released on its own.
const StudiesPanel = ({ appointment, onAppointmentChanged, refreshKey }) => {
  const { can } = useAuth();
  const { toast, branding, loadBranding } = useApp();
  const [studies, setStudies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!appointment?._id) return;
    try { setStudies((await radStudyApi.byAppointment(appointment._id))?.studies || []); }
    catch { setStudies([]); }
  }, [appointment?._id]);

  useEffect(() => { load(); loadBranding(); }, [load, refreshKey, loadBranding]);

  const sync = async () => {
    setBusy(true);
    try {
      const res = await radStudyApi.init(appointment._id);
      setStudies(res.studies || []);
      const missing = res.missingTemplates || [];
      toast.success(`${res.created?.length ?? 0} study created${missing.length ? ` · no scan template for ${missing.map((m) => m.name || m).join(", ")}` : ""}`);
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };

  const rooms = branding?.branding?.radiologyRooms || [];

  return (
    <Section
      title={`Scans · ${studies.length}`}
      right={!TERMINAL.has(appointment?.status) && can(...GATE.book) && (
        <Button size="sm" loading={busy} onClick={sync} title="Create any missing studies for the ordered scans">Sync scans</Button>
      )}
    >
      {studies.length === 0 ? (
        <span className="lab-muted">No studies. A scan SKU produces one only once an admin links a scan template to it — use Sync scans after fixing the catalogue.</span>
      ) : (
        <table className="lab-lines">
          <thead><tr><th>Scan</th><th>Status</th><th>Images</th><th>Reported by</th><th /></tr></thead>
          <tbody>
            {studies.map((s) => (
              <tr key={s._id}>
                <td>
                  <b>{s.templateSnapshot?.name || modalityLabel(s.modality)}</b>
                  <div className="lab-muted" style={{ fontSize: 11 }}>
                    {modalityLabel(s.modality)}{s.bodyPart ? ` · ${s.bodyPart}` : ""}{s.laterality ? ` · ${s.laterality}` : ""}{s.accession ? ` · ${s.accession}` : ""}
                  </div>
                </td>
                <td><span className="lab-row"><Badge kind={s.status}>{RAD_STATUS_LABEL[s.status] || s.status}</Badge>{s.revision > 1 && <Badge kind="IN_PROCESS">rev {s.revision}</Badge>}</span></td>
                <td className="lab-muted">{s.dicom?.instanceCount || "—"}</td>
                <td className="lab-muted">{s.reportedBy?.name || "—"}</td>
                <td>
                  <Button size="xs" variant={s.status === "PUBLISHED" ? "ghost" : "navy"} onClick={() => setSelected(s._id)}>
                    {s.status === "PUBLISHED" ? "View" : s.status === "PENDING" ? "Scan" : s.status === "VERIFIED" ? "Publish" : "Report"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <StudyDetail
        id={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        rooms={rooms}
        onChanged={() => { load(); onAppointmentChanged?.(); }}
      />
    </Section>
  );
};

export default StudiesPanel;
